import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Pie, Bar, Line, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip as ChartJSTooltip, ArcElement, Legend } from "chart.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


import "./QuoteyDashboard.css";
import { getSortIndicator, sortData } from "./sortingUtils";
import InsurerDetailModal from "./InsurerDetailModal";
import ReportModal from "./ReportModal"; // NEW IMPORT: Import the standalone ReportModal

import { saveAs } from 'file-saver';
// REASON FOR CHANGE: The 'xlsx' library is used for creating Excel files.
// We use 'utils' to create a worksheet from our data array and 'write' to generate the final file buffer.
import { utils, write } from 'xlsx';


ChartJS.register( CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartJSTooltip, ArcElement, Legend );

const fetchApi = async (urlPath, options = {}) => {
    const serviceUrl = process.env.REACT_APP_BASICSERVICE;
    const url = `${serviceUrl}${urlPath}`;
    const token = localStorage.getItem("jwt");
    if (!token) throw new Error("Authentication token not found.");
    const response = await fetch(url, {
        method: "GET", headers: { "Content-Type": "application/json", "x-auth-token": token }, ...options,
    });
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`API Error (${response.status}): ${response.statusText} - ${errorBody}`);
    }
    return response.json();
  };

const PolicyTab = () => {
  const [policyStats, setPolicyStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [paginatedPolicies, setPaginatedPolicies] = useState({ policies: [], pagination: { page: 1, limit: 10, total: 0, pages: 1 }});
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(true);
  const [policiesError, setPoliciesError] = useState(null);
  const [reasonsPagination, setReasonsPagination] = useState({ page: 1, limit: 10 });


  const [chartData, setChartData] = useState({
    policiesByInsurer: {}, topVerticalsForInsurers: {}, topInsurersForBinders: {},
    topInsurersForQuotes: {}, topVerticalsForQuotedPolicies: {},
    policiesByPaymentMethod: {},
    totalPremiumByVertical: {}
  });

  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  
  const [filters, setFilters] = useState({
    statusId: "", insurer: "", brokerId: "", search: "",
    vertical: "",
    policyStartDate: null, policyEndDate: null,
    createdStartDate: null, createdEndDate: null, 
    effectiveStartDate: null, effectiveEndDate: null,
    expiryStartDate: null, expiryEndDate: null,
    clientName: "", clientEmail: "", clientPhoneNumber: "",
  });
  
  const [sortConfig, setSortConfig] = useState({ key: "updatedAt", direction: "descending" });

  const [policyStatuses, setPolicyStatuses] = useState([]);
  const [brokersList, setBrokersList] = useState([]);
  const [verticalsList, setVerticalsList] = useState([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState(true);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportData, setReportData] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  const [selectedInsurerNameForDetail, setSelectedInsurerNameForDetail] = useState(null);
  const [selectedInsurerCodeForDetail, setSelectedInsurerCodeForDetail] = useState(null);
  const [showInsurerDetailModal, setShowInsurerDetailModal] = useState(false);
  const [insurerDetailData, setInsurerDetailData] = useState(null);
  const [isLoadingInsurerDetail, setIsLoadingInsurerDetail] = useState(false);
  const [insurerDetailError, setInsurerDetailError] = useState(null);

  const [activityReportData, setActivityReportData] = useState(null);
  const [isLoadingActivityReport, setIsLoadingActivityReport] = useState(false);
  const [activityReportError, setActivityReportError] = useState(null);

  const handleReasonsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (activityReportData?.reasonsBreakdown?.pagination?.pages || 1)) {
        setReasonsPagination(p => ({ ...p, page: newPage }));
    }
  };


  const fetchPolicyStatsCallback = useCallback(async () => {
    setIsLoadingStats(true); setStatsError(null);
    try {
      let qp = [];
      // Date filters
      if(filters.policyStartDate) qp.push(`policyStartDate=${filters.policyStartDate.toISOString().split("T")[0]}`);
      if(filters.policyEndDate) {
          const ied = new Date(filters.policyEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.createdStartDate) qp.push(`createdStartDate=${filters.createdStartDate.toISOString().split("T")[0]}`);
      if(filters.createdEndDate) {
          const ied = new Date(filters.createdEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.effectiveStartDate) qp.push(`effectiveStartDate=${filters.effectiveStartDate.toISOString().split("T")[0]}`);
      if(filters.effectiveEndDate) {
          const ied = new Date(filters.effectiveEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.expiryStartDate) qp.push(`expiryStartDate=${filters.expiryStartDate.toISOString().split("T")[0]}`);
      if(filters.expiryEndDate) {
          const ied = new Date(filters.expiryEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`expiryEndDate=${ied.toISOString().split("T")[0]}`);
      }
      
      // Other filters
      if(filters.statusId) qp.push(`statusId=${filters.statusId}`);
      if(filters.insurer) qp.push(`insurer=${encodeURIComponent(filters.insurer)}`);
      if(filters.brokerId) qp.push(`brokerId=${filters.brokerId}`);
      if(filters.vertical) qp.push(`vertical=${encodeURIComponent(filters.vertical)}`);
      if(filters.search) qp.push(`search=${encodeURIComponent(filters.search)}`);
      if(filters.clientName) qp.push(`clientName=${encodeURIComponent(filters.clientName)}`);
      if(filters.clientEmail) qp.push(`clientEmail=${encodeURIComponent(filters.clientEmail)}`);
      if(filters.clientPhoneNumber) qp.push(`clientPhoneNumber=${encodeURIComponent(filters.clientPhoneNumber)}`);
      
      const data = await fetchApi(`/api/policies/getPolicyStats?${qp.join('&')}`);
      setPolicyStats(data.stats);
    } catch (e) { setStatsError(e.message); setPolicyStats(null); }
    finally { setIsLoadingStats(false); }
  }, [filters]);


  // CHANGED: This function now sends the new 'Created Date' filter to the backend for the main policy table.
  const fetchPaginatedPoliciesCallback = useCallback(async () => {
    setIsLoadingPolicies(true); setPoliciesError(null);
    try {
      let qp = [`page=${pagination.page}`, `limit=${pagination.limit}`];
      if (filters.statusId) qp.push(`statusId=${filters.statusId}`);
      if (filters.insurer) qp.push(`insurer=${encodeURIComponent(filters.insurer)}`);
      if (filters.brokerId) qp.push(`brokerId=${filters.brokerId}`);
      if (filters.search) qp.push(`search=${encodeURIComponent(filters.search)}`);
      
      // Date filters
      if (filters.policyStartDate) qp.push(`policyStartDate=${filters.policyStartDate.toISOString().split("T")[0]}`);
      if (filters.policyEndDate) { const ied = new Date(filters.policyEndDate); ied.setDate(ied.getDate()+1); qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`); }
      
      // NEW: Send Created Date filter
      if (filters.createdStartDate) qp.push(`createdStartDate=${filters.createdStartDate.toISOString().split("T")[0]}`);
      if (filters.createdEndDate) { const ied = new Date(filters.createdEndDate); ied.setDate(ied.getDate()+1); qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`); }

      if (filters.effectiveStartDate) qp.push(`effectiveStartDate=${filters.effectiveStartDate.toISOString().split("T")[0]}`);
      if (filters.effectiveEndDate) { const ied = new Date(filters.effectiveEndDate); ied.setDate(ied.getDate()+1); qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`); }

      if (filters.expiryStartDate) qp.push(`expiryStartDate=${filters.expiryStartDate.toISOString().split("T")[0]}`);
      if (filters.expiryEndDate) { const ied = new Date(filters.expiryEndDate); ied.setDate(ied.getDate()+1); qp.push(`expiryEndDate=${ied.toISOString().split("T")[0]}`); }
      
      if (filters.vertical) qp.push(`vertical=${encodeURIComponent(filters.vertical)}`);
      if (filters.clientName) qp.push(`clientName=${encodeURIComponent(filters.clientName)}`);
      if (filters.clientEmail) qp.push(`clientEmail=${encodeURIComponent(filters.clientEmail)}`);
      if (filters.clientPhoneNumber) qp.push(`clientPhoneNumber=${encodeURIComponent(filters.clientPhoneNumber)}`);
      
      qp.push(`sortBy=${sortConfig.key}&sortDir=${sortConfig.direction}`);
      const data = await fetchApi(`/api/policies/getPoliciesData?${qp.join('&')}`);
      setPaginatedPolicies(data);
      if (data.pagination) setPagination(prev => ({ ...prev, page: data.pagination.page, limit: data.pagination.limit }));
    } catch (e) {
        console.error("Error fetching policies:", e);
        setPoliciesError(e.message);
        setPaginatedPolicies({ policies: [], pagination: { page: 1, limit: 10, total: 0, pages: 1 }});
    } finally { setIsLoadingPolicies(false); }
  }, [pagination.page, pagination.limit, filters, sortConfig]);

  // CHANGED: This function now sends all applicable filters for insurer detail popups.
  const fetchInsurerDetailsCallback = useCallback(async (insurerName) => {
    if (!insurerName) return;
    setIsLoadingInsurerDetail(true);
    setInsurerDetailError(null);
    setInsurerDetailData(null);
    try {
      let qp = [];
      // Date filters
      if(filters.policyStartDate) qp.push(`policyStartDate=${filters.policyStartDate.toISOString().split("T")[0]}`);
      if(filters.policyEndDate) {
          const ied = new Date(filters.policyEndDate); ied.setDate(ied.getDate()+1);
          qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.createdStartDate) qp.push(`createdStartDate=${filters.createdStartDate.toISOString().split("T")[0]}`);
      if(filters.createdEndDate) {
          const ied = new Date(filters.createdEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.effectiveStartDate) qp.push(`effectiveStartDate=${filters.effectiveStartDate.toISOString().split("T")[0]}`);
      if(filters.effectiveEndDate) {
          const ied = new Date(filters.effectiveEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`);
      }
      if(filters.expiryStartDate) qp.push(`expiryStartDate=${filters.expiryStartDate.toISOString().split("T")[0]}`);
      if(filters.expiryEndDate) {
          const ied = new Date(filters.expiryEndDate);
          ied.setDate(ied.getDate()+1);
          qp.push(`expiryEndDate=${ied.toISOString().split("T")[0]}`);
      }

      // Other filters
      if(filters.statusId) qp.push(`statusId=${filters.statusId}`);
      if(filters.brokerId) qp.push(`brokerId=${filters.brokerId}`);
      if(filters.vertical) qp.push(`vertical=${encodeURIComponent(filters.vertical)}`);
      if(filters.search) qp.push(`search=${encodeURIComponent(filters.search)}`);
      if(filters.clientName) qp.push(`clientName=${encodeURIComponent(filters.clientName)}`);
      if(filters.clientEmail) qp.push(`clientEmail=${encodeURIComponent(filters.clientEmail)}`);
      if(filters.clientPhoneNumber) qp.push(`clientPhoneNumber=${encodeURIComponent(filters.clientPhoneNumber)}`);
      
      const data = await fetchApi(`/api/policies/getInsurerDetails/${encodeURIComponent(insurerName)}?${qp.join('&')}`);
      setInsurerDetailData(data.stats);
      setSelectedInsurerCodeForDetail(data.insurerCode || null);
    } catch (e) {
      console.error(`Error fetching insurer details for ${insurerName}:`, e);
      setInsurerDetailError(e.message);
    } finally {
      setIsLoadingInsurerDetail(false);
    }
  }, [filters]);

  useEffect(() => {
    const fetchFilterDropdownData = async () => {
        setIsLoadingFilterData(true);
        try {
           const verticalsData = await fetchApi('/api/policies/getVerticalsList');
           setVerticalsList(verticalsData.verticals || []);
            const statusesData = await fetchApi('/api/policy-status');
            setPolicyStatuses(statusesData.policyStatuses || statusesData.policystatus || []);
            const brokersData = await fetchApi('/api/users?role=broker');
            setBrokersList(brokersData.users || []);
        } catch (error) {
            console.error("Failed to fetch filter dropdown data:", error);
        } finally { setIsLoadingFilterData(false); }
    };
    fetchFilterDropdownData();
  }, []);


  useEffect(() => { fetchPolicyStatsCallback(); }, [fetchPolicyStatsCallback]);
  useEffect(() => { fetchPaginatedPoliciesCallback(); }, [fetchPaginatedPoliciesCallback]);

  useEffect(() => {
    if (selectedInsurerNameForDetail && showInsurerDetailModal) {
      fetchInsurerDetailsCallback(selectedInsurerNameForDetail);
    }
  }, [selectedInsurerNameForDetail, showInsurerDetailModal, fetchInsurerDetailsCallback]);


  useEffect(() => {
    if (policyStats) {
      const getBarChartData = (dataArray, labelField, dataField, chartLabel) => ({
          labels: dataArray?.map(item => item[labelField] || 'Unknown').slice(0,10) || [],
          datasets: [{ label: chartLabel, data: dataArray?.map(item => item[dataField] || 0).slice(0,10) || [], backgroundColor: '#36A2EB', borderColor: '#36A2EB', borderWidth: 1 }]
      });
      const getNewChartColor = (index) => {
          const colors = ['#FF6384', '#4BC0C0', '#FFCD56', '#9966FF', '#FF9F40', '#36A2EB', '#A6C844', '#7B68EE'];
          return colors[index % colors.length];
      };

      setChartData({
        policiesByInsurer: getBarChartData(policyStats.policiesByInsurer, 'insurer', 'count', 'Policies by Insurer'),
        topVerticalsForInsurers: getBarChartData(policyStats.topVerticalsForInsurers, 'vertical', 'totalPolicies', 'Policies by Vertical'),
        topInsurersForBinders: getBarChartData(policyStats.topInsurersForBinders, 'insurer', 'count', 'Bound Policies by Insurer'),
        topInsurersForQuotes: getBarChartData(policyStats.topInsurersForQuotes, 'insurer', 'count', 'Quoted Policies by Insurer'),
        topVerticalsForQuotedPolicies: getBarChartData(policyStats.topVerticalsForQuotedPolicies, 'vertical', 'count', 'Quotes by Vertical'),
        policiesByPaymentMethod: {
            labels: policyStats.policiesByPaymentMethod?.map(item => item.paymentMethod || 'Unknown').slice(0,10) || [],
            datasets: [{
                label: 'Policies by Payment Method',
                data: policyStats.policiesByPaymentMethod?.map(item => item.count || 0).slice(0,10) || [],
                backgroundColor: policyStats.policiesByPaymentMethod?.map((_, i) => getNewChartColor(i + 2)) || [],
                borderColor: policyStats.policiesByPaymentMethod?.map((_, i) => getNewChartColor(i + 2)) || [],
                borderWidth: 1,
            }]
        },
        totalPremiumByVertical: {
            labels: policyStats.totalPremiumByVertical?.map(item => item.vertical || 'Unknown').slice(0,10) || [],
            datasets: [{
                label: 'Total Premium by Vertical',
                data: policyStats.totalPremiumByVertical?.map(item => item.totalPremium || 0).slice(0,10) || [],
                backgroundColor: policyStats.totalPremiumByVertical?.map((_, i) => getNewChartColor(i + 4)) || [],
                borderColor: policyStats.totalPremiumByVertical?.map((_, i) => getNewChartColor(i + 4)) || [],
                borderWidth: 1,
            }]
        }
      });
    }
  }, [policyStats]);

  const handleFilterChange = (filterName, value) => { setFilters(prev => ({ ...prev, [filterName]: value })); setPagination(prev => ({ ...prev, page: 1 })); };
  const handleDateFilterChange = (filterNameStart, filterNameEnd, dates) => { const [start, end] = dates || [null, null]; setFilters(prev => ({ ...prev, [filterNameStart]: start, [filterNameEnd]: end })); setPagination(prev => ({ ...prev, page: 1 })); };
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') { direction = 'descending'; } 
    else if (sortConfig.key !== key) { direction = 'ascending'; }
    setSortConfig({ key, direction });
  };
  const handlePageChange = (newPage) => { if(newPage >= 1 && newPage <= (paginatedPolicies.pagination?.pages || 1)) setPagination(p=>({...p,page:newPage}));};
  const handleLimitChange = (e) => setPagination({page:1,limit:parseInt(e.target.value)});
  const handleOpenInsurerDetail = (insurerName) => { setSelectedInsurerNameForDetail(insurerName); setShowInsurerDetailModal(true); };
  const handleCloseInsurerDetail = () => { setShowInsurerDetailModal(false); setSelectedInsurerNameForDetail(null); setSelectedInsurerCodeForDetail(null); setInsurerDetailData(null); setInsurerDetailError(null); };

  const policiesToDisplay = paginatedPolicies.policies || [];

  // CHANGED: This function now sends all applicable date filters for generating full reports.
  const fetchFullReportCallback = useCallback(async (type) => {
    setIsLoadingReport(true); setReportError(null); setReportData([]);
    setReportType(type); setShowReportModal(true);
    try {
      let qp = [`type=${type}`];
      // Policy Date
      if(filters.policyStartDate) qp.push(`policyStartDate=${filters.policyStartDate.toISOString().split("T")[0]}`);
      if(filters.policyEndDate) {
           const ied = new Date(filters.policyEndDate);
           ied.setDate(ied.getDate()+1);
           qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`);
      }
      // Created Date
      if(filters.createdStartDate) qp.push(`createdStartDate=${filters.createdStartDate.toISOString().split("T")[0]}`);
      if(filters.createdEndDate) {
           const ied = new Date(filters.createdEndDate);
           ied.setDate(ied.getDate()+1);
           qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`);
      }
      // Effective Date
      if(filters.effectiveStartDate) qp.push(`effectiveStartDate=${filters.effectiveStartDate.toISOString().split("T")[0]}`);
      if(filters.effectiveEndDate) {
           const ied = new Date(filters.effectiveEndDate);
           ied.setDate(ied.getDate()+1);
           qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`);
      }
      // Expiry Date
      if(filters.expiryStartDate) qp.push(`expiryStartDate=${filters.expiryStartDate.toISOString().split("T")[0]}`);
      if(filters.expiryEndDate) {
           const ied = new Date(filters.expiryEndDate);
           ied.setDate(ied.getDate()+1);
           qp.push(`expiryEndDate=${ied.toISOString().split("T")[0]}`);
      }

      // Non-date filters
      if(filters.statusId) qp.push(`statusId=${filters.statusId}`);
      if(filters.vertical) qp.push(`vertical=${encodeURIComponent(filters.vertical)}`);
      if(filters.insurer) qp.push(`insurer=${encodeURIComponent(filters.insurer)}`);
      if(filters.brokerId) qp.push(`brokerId=${filters.brokerId}`);
      if(filters.search) qp.push(`search=${encodeURIComponent(filters.search)}`);
      if(filters.clientName) qp.push(`clientName=${encodeURIComponent(filters.clientName)}`);
      if(filters.clientEmail) qp.push(`clientEmail=${encodeURIComponent(filters.clientEmail)}`);
      if(filters.clientPhoneNumber) qp.push(`clientPhoneNumber=${encodeURIComponent(filters.clientPhoneNumber)}`);

      const result = await fetchApi(`/api/policies/getPoliciesReport?${qp.join('&')}`);
      setReportData(result.data);
    } catch (e) { setReportError(e.message); }
    finally { setIsLoadingReport(false); }
  }, [filters.policyStartDate, filters.policyEndDate, filters.createdStartDate, filters.createdEndDate, filters.effectiveStartDate, filters.effectiveEndDate, filters.expiryStartDate, filters.expiryEndDate, filters.statusId, filters.vertical, filters.insurer, filters.brokerId, filters.search, filters.clientName, filters.clientEmail, filters.clientPhoneNumber]);

  const chartOptions = { responsive:true, maintainAspectRatio:false, plugins:{legend:{position:'top', labels: {boxWidth: 12, padding:10}},title:{display:false}}, scales:{y:{beginAtZero:true}, x:{ticks:{autoSkip:false, maxRotation:45, minRotation:30, font: {size: 10}}}}};

  const policiesByInsurerChartOptions = useMemo(() => ({
    ...chartOptions,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        const insurerName = chartData.policiesByInsurer.labels[clickedIndex];
        if (insurerName) { handleOpenInsurerDetail(insurerName); }
      }
    },
    plugins: { ...chartOptions.plugins, tooltip: { callbacks: { afterLabel: function(context) { return 'Click for details'; } } } }
  }), [chartOptions, chartData.policiesByInsurer, handleOpenInsurerDetail]);

  // CHANGED: This function now sends all filters to the backend for comprehensive filtering.
  const fetchOverallActivityReport = useCallback(async () => {
    setIsLoadingActivityReport(true);
    setActivityReportError(null);
    try {
        let qp = [];
        // Date filters
        if(filters.policyStartDate) qp.push(`policyStartDate=${filters.policyStartDate.toISOString().split("T")[0]}`);
        if(filters.policyEndDate) {
            const ied = new Date(filters.policyEndDate);
            ied.setDate(ied.getDate()+1);
            qp.push(`policyEndDate=${ied.toISOString().split("T")[0]}`);
        }
        if(filters.createdStartDate) qp.push(`createdStartDate=${filters.createdStartDate.toISOString().split("T")[0]}`);
        if(filters.createdEndDate) {
            const ied = new Date(filters.createdEndDate);
            ied.setDate(ied.getDate()+1);
            qp.push(`createdEndDate=${ied.toISOString().split("T")[0]}`);
        }
        if(filters.effectiveStartDate) qp.push(`effectiveStartDate=${filters.effectiveStartDate.toISOString().split("T")[0]}`);
        if(filters.effectiveEndDate) {
            const ied = new Date(filters.effectiveEndDate);
            ied.setDate(ied.getDate()+1);
            qp.push(`effectiveEndDate=${ied.toISOString().split("T")[0]}`);
        }
        if(filters.expiryStartDate) qp.push(`expiryStartDate=${filters.expiryStartDate.toISOString().split("T")[0]}`);
        if(filters.expiryEndDate) {
            const ied = new Date(filters.expiryEndDate);
            ied.setDate(ied.getDate()+1);
            qp.push(`expiryEndDate=${ied.toISOString().split("T")[0]}`);
        }
        
        // Other filters
        if(filters.statusId) qp.push(`statusId=${filters.statusId}`);
        if(filters.insurer) qp.push(`insurer=${encodeURIComponent(filters.insurer)}`);
        if(filters.brokerId) qp.push(`brokerId=${filters.brokerId}`);
        if(filters.vertical) qp.push(`vertical=${encodeURIComponent(filters.vertical)}`);
        if(filters.search) qp.push(`search=${encodeURIComponent(filters.search)}`);
        if(filters.clientName) qp.push(`clientName=${encodeURIComponent(filters.clientName)}`);
        if(filters.clientEmail) qp.push(`clientEmail=${encodeURIComponent(filters.clientEmail)}`);
        if(filters.clientPhoneNumber) qp.push(`clientPhoneNumber=${encodeURIComponent(filters.clientPhoneNumber)}`);
        
        qp.push(`reasonsPage=${reasonsPagination.page}`);
        qp.push(`reasonsLimit=${reasonsPagination.limit}`);
        const data = await fetchApi(`/api/policies/getOverallActivityReport?${qp.join('&')}`);
        setActivityReportData(data);
    } catch (e) {
        console.error("Error fetching activity report data:", e);
        setActivityReportError(e.message); setActivityReportData(null);
    } finally { setIsLoadingActivityReport(false); }
  }, [filters, reasonsPagination]);

  // CHANGED: The Excel export now dynamically lists all active date filters for better report clarity.
  const handleExportExcel = useCallback((reportType) => {
    if (!activityReportData) {
      alert("No report data available. Please load the report first.");
      return;
    }

    let dataToExport, totals, title, entityColumnName, descriptionText;

    if (reportType === 'brokerage') {
      dataToExport = activityReportData.brokerageReport;
      totals = activityReportData.brokerageTotals;
      title = "ACTIVITY REPORT BY BROKERAGE";
      entityColumnName = "Brokerage";
      descriptionText = "The intent of this report is to get a clear view of prospects, quotes, binders and premium bound by brokerage.";
    } else if (reportType === 'vertical') {
      dataToExport = activityReportData.verticalReport;
      totals = activityReportData.verticalTotals;
      title = "ACTIVITY REPORT BY VERTICAL";
      entityColumnName = "Vertical";
      descriptionText = "The intent of this report is to get a clear view of prospects, quotes, binders and premium bound by vertical.";
    } else if (reportType === 'insurer') {
      dataToExport = activityReportData.insurerReport;
      totals = activityReportData.insurerTotals;
      title = "ACTIVITY REPORT BY INSURER";
      entityColumnName = "Insurer";
      descriptionText = "The intent of this report is to get a clear view of prospects, quotes, binders and premium bound by insurer.";
    } else {
      alert("Invalid report type for export.");
      return;
    }

    const headers = [entityColumnName, "Prospects Created", "Quotes Received", "Binders Created", "Premium Bound"];
    const rows = dataToExport.map(item => [item.entityName, item.prospectsCreated, item.quotesReceived, item.bindersCreated, item.premiumBound]);
    const totalsRow = ["Total", totals.prospectsCreated, totals.quotesReceived, totals.bindersCreated, totals.premiumBound];
    
    // NEW: Dynamically build a string of all active date filters.
    const dateFilters = [
        { label: 'Policy', start: filters.policyStartDate, end: filters.policyEndDate },
        { label: 'Created', start: filters.createdStartDate, end: filters.createdEndDate },
        { label: 'Effective', start: filters.effectiveStartDate, end: filters.effectiveEndDate },
        { label: 'Expiry', start: filters.expiryStartDate, end: filters.expiryEndDate },
    ];
    const activeDateFilters = dateFilters
        .filter(f => f.start || f.end)
        .map(f => `${f.label}: ${f.start ? f.start.toLocaleDateString() : 'N/A'} to ${f.end ? f.end.toLocaleDateString() : 'N/A'}`)
        .join('  |  ');

    const ws_data = [
      [title], [], [descriptionText],
      ["It will be created in Excel and be based on whatever date selection was filtered when creating the report"], [],
      [`Date Filters: ${activeDateFilters || 'None Applied'}`], [],
      headers, ...rows, totalsRow
    ];

    if (activityReportData?.reasonsBreakdown?.data?.length > 0) {
      ws_data.push(
          [], [], ["POLICY REASONS BREAKDOWN"],
          [`This section details the reasons for policies being bound/declined/referred. The export contains the currently visible page of reasons.`], [],
          ["Reason", "Count"]
      );
      activityReportData.reasonsBreakdown.data.forEach(reasonItem => { ws_data.push([reasonItem.reason, reasonItem.count]); });
      ws_data.push(["Total Unique Reasons", activityReportData.reasonsBreakdown.pagination.total]);
    }

    const ws = utils.aoa_to_sheet(ws_data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Activity Report");

    const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    saveAs(blob, `${reportType.toLowerCase().replace(/\s/g, '_')}_activity_report_${new Date().toISOString().split('T')[0]}.xlsx`);

  }, [activityReportData, filters]);


  useEffect(() => { fetchOverallActivityReport(); }, [fetchOverallActivityReport]);


  return (
    <div className="tab-content">
       <div className="table-filters" style={{flexWrap: 'wrap', marginBottom: '20px', padding: '10px 15px', background:'#f0f2f5', borderRadius:'8px', display: 'flex', gap: '15px', alignItems: 'center'}}>
        <div className="filter-item"><label>Policy Dates:</label><DatePicker selectsRange startDate={filters.policyStartDate} endDate={filters.policyEndDate} onChange={(d)=>handleDateFilterChange('policyStartDate','policyEndDate',d)} isClearable placeholderText="Start - End" dateFormat="yyyy-MM-dd" className="filter-select"/></div>
        {/* NEW: DatePicker for the Created At filter */}
        <div className="filter-item"><label>Created Dates:</label><DatePicker selectsRange startDate={filters.createdStartDate} endDate={filters.createdEndDate} onChange={(d)=>handleDateFilterChange('createdStartDate','createdEndDate',d)} isClearable placeholderText="Start - End" dateFormat="yyyy-MM-dd" className="filter-select"/></div>
        <div className="filter-item"><label>Effective Dates:</label><DatePicker selectsRange startDate={filters.effectiveStartDate} endDate={filters.effectiveEndDate} onChange={(d)=>handleDateFilterChange('effectiveStartDate','effectiveEndDate',d)} isClearable placeholderText="Start - End" dateFormat="yyyy-MM-dd" className="filter-select"/></div>
        <div className="filter-item"><label>Expiry Dates:</label><DatePicker selectsRange startDate={filters.expiryStartDate} endDate={filters.expiryEndDate} onChange={(d)=>handleDateFilterChange('expiryStartDate','expiryEndDate',d)} isClearable placeholderText="Start - End" dateFormat="yyyy-MM-dd" className="filter-select"/></div>
        <div className="filter-item"><label>Insurer:</label><input type="text" className="search-input" placeholder="Insurer name" value={filters.insurer} onChange={(e)=>handleFilterChange('insurer', e.target.value)}/></div>
        {/* <div className="filter-item"><label>Broker:</label><select className="filter-select" value={filters.brokerId} onChange={(e)=>handleFilterChange('brokerId', e.target.value)} disabled={isLoadingFilterData}><option value="">All Brokers</option>{brokersList.map(b=><option key={b._id} value={b._id}>{b.name || b.email}</option>)}</select></div> */}
        <div className="filter-item">
            <label>Vertical:</label>
            <select className="filter-select" value={filters.vertical} onChange={(e)=>handleFilterChange('vertical', e.target.value)} disabled={isLoadingFilterData}>
                <option value="">All Verticals</option>
                {verticalsList.map(v=><option key={v.id} value={v.name.en || v.name}>{v.name.en || v.name}</option>)}
            </select>
        </div>
        <div className="filter-item"><label>Client Name:</label><input type="text" className="search-input" placeholder="Client name" value={filters.clientName} onChange={(e)=>handleFilterChange('clientName', e.target.value)}/></div>
        <div className="filter-item"><label>Client Email:</label><input type="text" className="search-input" placeholder="Client email" value={filters.clientEmail} onChange={(e)=>handleFilterChange('clientEmail', e.target.value)}/></div>
        <div className="filter-item"><label>Client Phone:</label><input type="text" className="search-input" placeholder="Client phone" value={filters.clientPhoneNumber} onChange={(e)=>handleFilterChange('clientPhoneNumber', e.target.value)}/></div>
        <div className="filter-item" style={{flexGrow: 1}}><label>Search Ref/Binder:</label><input type="search" className="search-input" style={{width:'100%'}} placeholder="Quote Ref, Binder #" value={filters.search} onChange={(e)=>handleFilterChange('search', e.target.value)}/></div>
      </div>
      {isLoadingStats ? (
        <div className="loading-cell"><div className="loading-spinner"/>Loading Stats...</div>
      ) : statsError ? (
        <div className="error-message">Statistics Error: {statsError}</div>
      ) : (
        <div className="stats-overview">
          <div className="stat-card"><h3>Total Policies</h3><p className="stat-value">{policyStats?.totalPolicies ?? 0}</p></div>
          <div className="stat-card"><h3>Avg. Policies / Insurer</h3><p className="stat-value">{!policyStats?.policiesByInsurer?.length ? 'N/A' : (policyStats.totalPolicies / policyStats.policiesByInsurer.length).toFixed(1) }</p></div>
        </div>
      )}

     

      <div className="table-container report-download-section">
          <h3>Download Activity Reports (Excel)</h3>
          <p>Download a comprehensive activity report based on the selected filters. Each report includes a breakdown of policy reasons.</p>
          <div className="report-buttons-container">
              <button className="report-button" onClick={() => handleExportExcel('brokerage')} disabled={isLoadingActivityReport || !activityReportData}>Download by Brokerage</button>
              <button className="report-button" onClick={() => handleExportExcel('vertical')} disabled={isLoadingActivityReport || !activityReportData}>Download by Vertical</button>
              <button className="report-button" onClick={() => handleExportExcel('insurer')} disabled={isLoadingActivityReport || !activityReportData}>Download by Insurer</button>
          </div>
          {isLoadingActivityReport && <div className="loading-cell small"><div className="loading-spinner"/>Loading Report Data...</div>}
          {activityReportError && <div className="error-message">Could not load report data: {activityReportError}</div>}
      </div>


      <div className="table-container">
        <h3>Policy Reasons Breakdown ({activityReportData?.reasonsBreakdown?.pagination?.total || 0})</h3>
        {isLoadingActivityReport ? (
            <div className="loading-cell"><div className="loading-spinner" />Loading Reasons...</div>
        ) : activityReportError ? (
            <div className="error-message">Could not load reasons: {activityReportError}</div>
        ) : (
            <>
                <table className="data-table">
                    <thead><tr><th>Reason</th><th>Count</th></tr></thead>
                    <tbody>
                        {activityReportData?.reasonsBreakdown?.data?.length > 0 ? (
                            activityReportData.reasonsBreakdown.data.map((item, index) => (
                                <tr key={index}><td>{item.reason}</td><td>{item.count}</td></tr>
                            ))
                        ) : (
                            <tr><td colSpan="2" className="empty-data-cell">No reasons found matching criteria.</td></tr>
                        )}
                    </tbody>
                </table>
                <div className="pagination-container">
                    <div className="pagination-controls">
                        <button onClick={() => handleReasonsPageChange(reasonsPagination.page - 1)} disabled={reasonsPagination.page <= 1}>«</button>
                        <span> Page {reasonsPagination.page} of {activityReportData?.reasonsBreakdown?.pagination?.pages || 1} </span>
                        <button onClick={() => handleReasonsPageChange(reasonsPagination.page + 1)} disabled={reasonsPagination.page >= (activityReportData?.reasonsBreakdown?.pagination?.pages || 1)}>»</button>
                    </div>
                    <div className="pagination-info">
                        <span>Total: {activityReportData?.reasonsBreakdown?.pagination?.total || 0} reasons</span>
                    </div>
                </div>
            </>
        )}
      </div>

      <div className="charts-row">
        <div className="chart-container">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3>Policies by Insurer</h3>
            <button className="report-button" style={{padding:'4px 8px', fontSize:'12px'}} onClick={()=>fetchFullReportCallback('insurer')} disabled={isLoadingStats}>Full Report</button>
          </div>
          <div className="chart-wrapper">
            {isLoadingStats ? <div className="loading-cell">L...</div> : (chartData.policiesByInsurer?.labels?.length > 0 ? <Bar data={chartData.policiesByInsurer || {}} options={policiesByInsurerChartOptions} /> : <p className="empty-data-cell">No data available.</p>)}
          </div>
          {!isLoadingStats && chartData.policiesByInsurer?.labels?.length > 0 && (<div className="chart-note">Click bar for insurer details</div>)}
        </div>
        <div className="chart-container">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3>Top Verticals (All Policies)</h3>
            <button className="report-button" style={{padding:'4px 8px', fontSize:'12px'}} onClick={()=>fetchFullReportCallback('vertical')} disabled={isLoadingStats}>Full Report</button>
          </div>
          <div className="chart-wrapper">
            {isLoadingStats?<div className="loading-cell">L...</div>:(chartData.topVerticalsForInsurers?.labels?.length > 0 ? <Bar data={chartData.topVerticalsForInsurers || {}} options={chartOptions}/> : <p className="empty-data-cell">No data available.</p>)}
          </div>
        </div>
      </div>
     
       <div className="charts-row">
        <div className="chart-container full-width"><h3>Top Verticals for Quoted Policies</h3><div className="chart-wrapper">{isLoadingStats?<div className="loading-cell">L...</div>:(chartData.topVerticalsForQuotedPolicies?.labels?.length > 0 ? <Bar data={chartData.topVerticalsForQuotedPolicies || {}} options={chartOptions}/> : <p className="empty-data-cell">No data available.</p>)}</div></div>
      </div>
      <div className="charts-row">
        <div className="chart-container">
          <h3>Policies by Payment Method</h3>
          <div className="chart-wrapper">{isLoadingStats ? <div className="loading-cell">L...</div> : (chartData.policiesByPaymentMethod?.labels?.length > 0 ? <Bar data={chartData.policiesByPaymentMethod || {}} options={chartOptions} /> : <p className="empty-data-cell">No data available.</p>)}</div>
        </div>
        <div className="chart-container">
          <h3>Total Premium by Vertical</h3>
          <div className="chart-wrapper">{isLoadingStats ? <div className="loading-cell">L...</div> : (chartData.totalPremiumByVertical?.labels?.length > 0 ? <Bar data={chartData.totalPremiumByVertical || {}} options={chartOptions} /> : <p className="empty-data-cell">No data available.</p>)}</div>
        </div>
      </div>

      <div className="table-container">
        <h3>Policies ({paginatedPolicies.pagination?.total || 0})</h3>
        {policiesError && <div className="error-message">Policies List Error: {policiesError}</div>}
        <table className="data-table">
          <thead><tr>
            <th onClick={()=>requestSort('clientName')} className={sortConfig.key==='clientName'?'sorted':''}>Client Name {getSortIndicator('clientName',sortConfig)}</th>
            <th onClick={()=>requestSort('clientEmail')} className={sortConfig.key==='clientEmail'?'sorted':''}>Client Email {getSortIndicator('clientEmail',sortConfig)}</th>
            <th onClick={()=>requestSort('clientPhoneNumber')} className={sortConfig.key==='clientPhoneNumber'?'sorted':''}>Client Phone {getSortIndicator('clientPhoneNumber',sortConfig)}</th>
            <th onClick={()=>requestSort('insurer')} className={sortConfig.key==='insurer'?'sorted':''}>Insurer {getSortIndicator('insurer',sortConfig)}</th>
            <th onClick={()=>requestSort('statusName')} className={sortConfig.key==='statusName'?'sorted':''}>Status {getSortIndicator('statusName',sortConfig)}</th>
            <th onClick={()=>requestSort('policyReason')} className={sortConfig.key==='policyReason'?'sorted':''}>Reason {getSortIndicator('policyReason',sortConfig)}</th>
            <th onClick={()=>requestSort('vertical')} className={sortConfig.key==='vertical'?'sorted':''}>Vertical {getSortIndicator('vertical',sortConfig)}</th>
            <th onClick={()=>requestSort('brokerName')} className={sortConfig.key==='brokerName'?'sorted':''}>Broker {getSortIndicator('brokerName',sortConfig)}</th>
            <th onClick={()=>requestSort('startdate')} className={sortConfig.key==='startdate'?'sorted':''}>Start Date {getSortIndicator('startdate',sortConfig)}</th>
            {/* NEW: Column for Created At Date */}
            <th onClick={()=>requestSort('createdAt')} className={sortConfig.key==='createdAt'?'sorted':''}>Created Date {getSortIndicator('createdAt',sortConfig)}</th>
            <th onClick={()=>requestSort('effectivedate')} className={sortConfig.key==='effectivedate'?'sorted':''}>Effective {getSortIndicator('effectivedate',sortConfig)}</th>
            <th onClick={()=>requestSort('expirydate')} className={sortConfig.key==='expirydate'?'sorted':''}>Expiry {getSortIndicator('expirydate',sortConfig)}</th>
            <th onClick={()=>requestSort('quoteref')} className={sortConfig.key==='quoteref'?'sorted':''}>Quote Ref {getSortIndicator('quoteref',sortConfig)}</th>
            <th onClick={()=>requestSort('bindernumber')} className={sortConfig.key==='bindernumber'?'sorted':''}>Binder# {getSortIndicator('bindernumber',sortConfig)}</th>
            <th onClick={()=>requestSort('updatedAt')} className={sortConfig.key==='updatedAt'?'sorted':''}>Last Updated {getSortIndicator('updatedAt',sortConfig)}</th>
          </tr></thead>
          <tbody>
            {isLoadingPolicies ? (<tr><td colSpan="15" className="loading-cell"><div className="loading-spinner"/>Loading Policies...</td></tr>) 
            : policiesError ? (<tr><td colSpan="15" className="empty-data-cell">Could not load policies.</td></tr>) 
            : policiesToDisplay.length === 0 ? (<tr><td colSpan="15" className="empty-data-cell">No policies found matching criteria.</td></tr>)
            : (policiesToDisplay.map((p)=>(
                <tr key={p._id}>
                  <td>{p.clientName || p.clientCompanyName || `${p.clientFirstName || ''} ${p.clientLastName || ''}`.trim() || 'N/A'}</td> 
                  <td>{p.clientEmail || 'N/A'}</td> 
                  <td>{p.clientPhoneNumber || 'N/A'}</td> 
                  <td>{p.insurer && p.insurer !== 'N/A' ? (<button className="brokerage-link" onClick={() => handleOpenInsurerDetail(p.insurer)}>{p.insurer}</button>) : 'N/A'}</td>
                  <td><span className={`status-badge status-${p.statusName?.toLowerCase().replace(/\s+/g,'-') || 'unknown'}`}>{p.statusName || 'N/A'}</span></td>
                  <td className="ellipsis-cell"><span title={p.policyReason || 'N/A'}>{p.policyReason || 'N/A'}</span></td>
                  <td>{p.vertical || 'N/A'}</td>
                  <td>{p.brokerName || p.brokerEmail || 'N/A'}</td>
                  <td>{p.startdate ? new Date(p.startdate).toLocaleDateString() : 'N/A'}</td>
                  {/* NEW: Cell to display the created date */}
                  <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A'}</td>
                  <td>{p.effectivedate ? new Date(p.effectivedate).toLocaleDateString() : 'N/A'}</td>
                  <td>{p.expirydate ? new Date(p.expirydate).toLocaleDateString() : 'N/A'}</td>
                  <td>{p.quoteref || 'N/A'}</td>
                  <td>{p.bindernumber || 'N/A'}</td>
                  <td>{new Date(p.updatedAt).toLocaleDateString()}</td>
                </tr>)))}
          </tbody>
        </table>
      </div>
      <div className="pagination-container">
        <div className="pagination-controls">
            <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>«</button>
            <span> Page {pagination.page} of {paginatedPolicies.pagination?.pages || 1} </span>
            <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= (paginatedPolicies.pagination?.pages || 1)}>»</button>
        </div>
        <div className="pagination-info">
            <span>Total: {paginatedPolicies.pagination?.total || 0} policies</span>
            <select className="pagination-limit" value={pagination.limit} onChange={handleLimitChange}>
                <option value="10">10/page</option><option value="25">25/page</option>
                <option value="50">50/page</option><option value="100">100/page</option>
            </select>
        </div>
      </div>

      <ReportModal show={showReportModal} onClose={()=>setShowReportModal(false)} title={`Full ${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`} data={reportData} isLoading={isLoadingReport} error={reportError} reportType={reportType} onItemClick={handleOpenInsurerDetail}/>
      <InsurerDetailModal show={showInsurerDetailModal} onClose={handleCloseInsurerDetail} insurerName={selectedInsurerNameForDetail} insurerCode={selectedInsurerCodeForDetail} data={insurerDetailData} isLoading={isLoadingInsurerDetail} error={insurerDetailError} chartOptions={chartOptions} />
    </div>
  );
};
export default PolicyTab;